import { useEffect, useState } from "react";
import {
  AFTER_MESSAGE_CHANGE,
  deleteMessage,
  reorderMessage,
  updateMessage,
} from "@/api/mutations";
import { useAccao, texto } from "@/api/useAccao";
import { Confirmacao } from "@/components/ui/Confirmacao";
import { longDate as dataLonga } from "@historia/shared/dates";
import type { MessageWithSrc as MensagemComSrc } from "@historia/shared/contracts";

type FotoResumo = { id: string; legenda: string | null };

const campo =
  "rounded-2xl border border-ouro/30 bg-creme-50 px-4 py-2.5 text-tinta outline-none transition-colors focus:border-ouro focus:ring-2 focus:ring-ouro/30";

/** Uma mensagem da coleção /para-sempre: cena tenra + controlos (em edição). */
export function MensagemEntrada({
  mensagem,
  fotos,
  indice,
  total,
}: {
  mensagem: MensagemComSrc;
  fotos: FotoResumo[];
  indice: number;
  total: number;
}) {
  const [aEditar, setAEditar] = useState(false);
  const [aApagar, setAApagar] = useState(false);

  const [estado, acao] = useAccao(
    (fd: FormData) =>
      updateMessage({
        id: mensagem.id,
        titulo: texto(fd, "titulo"),
        corpo: texto(fd, "corpo"),
        ocasiaoData: texto(fd, "ocasiao_data") || null,
        fotoDestaqueId: texto(fd, "foto_destaque_id") || null,
      }),
    { invalidate: AFTER_MESSAGE_CHANGE },
  );

  const [, apagar] = useAccao((id: string) => deleteMessage(id), {
    invalidate: AFTER_MESSAGE_CHANGE,
  });

  const [, mover] = useAccao(
    (direction: "up" | "down") => reorderMessage({ id: mensagem.id, direction }),
    { invalidate: AFTER_MESSAGE_CHANGE },
  );

  async function submeter(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await acao(new FormData(event.currentTarget));
  }

  useEffect(() => {
    if (aEditar && estado.ok) setAEditar(false);
  }, [estado.ok, aEditar]);

  // Formulário de edição aberto.
  if (aEditar) {
    return (
      <article className="rounded-3xl border border-ouro/15 bg-creme-50/80 p-6 shadow-sm sm:p-8">
        <form onSubmit={submeter} className="flex flex-col gap-3 text-left">
          <input type="hidden" name="id" value={mensagem.id} />
          <input
            name="titulo"
            required
            maxLength={120}
            defaultValue={mensagem.titulo}
            placeholder="Título / ocasião"
            className={`${campo} font-display text-lg`}
          />
          <textarea
            name="corpo"
            required
            rows={8}
            defaultValue={mensagem.corpo}
            placeholder="A mensagem… (deixa uma linha em branco entre parágrafos)"
            className={`${campo} resize-none leading-relaxed`}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              type="date"
              name="ocasiao_data"
              defaultValue={mensagem.ocasiao_data ?? ""}
              className={campo}
            />
            <select
              name="foto_destaque_id"
              defaultValue={mensagem.foto_destaque_id ?? ""}
              className={campo}
            >
              <option value="">— Sem foto —</option>
              {fotos.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legenda || "Foto sem legenda"}
                </option>
              ))}
            </select>
          </div>
          {estado.erro && <p className="text-sm text-tinta">{estado.erro}</p>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              className="rounded-full bg-ouro px-5 py-2 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600"
            >
              Guardar
            </button>
            <button
              type="button"
              onClick={() => setAEditar(false)}
              className="rounded-full px-4 py-2 text-sm font-semibold text-tinta-700 hover:bg-creme-200"
            >
              Cancelar
            </button>
          </div>
        </form>
      </article>
    );
  }

  // Vista normal (leitura). Disposição de livro: título no topo; depois foto à
  // esquerda e texto à direita (empilha no telemóvel). (IDEA-013 v2)
  const temFoto = !!mensagem.fotoSrc;
  return (
    <article className="rounded-3xl border border-ouro/15 bg-creme-50/80 px-6 py-8 text-left shadow-sm sm:px-10 sm:py-10">
      <header className="text-center">
        <h2 className="font-display text-2xl text-tinta sm:text-3xl">{mensagem.titulo}</h2>
        {mensagem.ocasiao_data && (
          <p className="mt-1 text-sm italic text-tinta-700/80">
            {dataLonga(mensagem.ocasiao_data)}
          </p>
        )}
      </header>

      <div className={`mt-6 grid items-start gap-8 ${temFoto ? "md:grid-cols-2" : ""}`}>
        {temFoto && (
          <figure className="md:sticky md:top-24">
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-ouro/20 shadow-md">
              <img
                src={mensagem.fotoSrc!}
                alt={mensagem.fotoLegenda ?? mensagem.titulo}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            {mensagem.fotoLegenda && (
              <figcaption className="mt-2 text-center text-sm italic text-tinta-700/80">
                {mensagem.fotoLegenda}
              </figcaption>
            )}
          </figure>
        )}

        <div className={temFoto ? "" : "mx-auto max-w-prose"}>
          {mensagem.corpo
            .split(/\n{2,}/)
            .map((p) => p.trim())
            .filter(Boolean)
            .map((paragrafo, i) => (
              <p key={i} className="mb-4 leading-loose text-tinta-700 last:mb-0">
                {paragrafo}
              </p>
            ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2 border-t border-ouro/10 pt-4">
        <button
          type="button"
          onClick={() => void mover("up")}
          disabled={indice === 0}
          aria-label="Mover para cima"
          className="rounded-full px-2.5 py-1 text-sm text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-30"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={() => void mover("down")}
          disabled={indice === total - 1}
          aria-label="Mover para baixo"
          className="rounded-full px-2.5 py-1 text-sm text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-30"
        >
          ↓
        </button>
        <button
          type="button"
          onClick={() => setAEditar(true)}
          className="rounded-full px-3 py-1 text-xs font-semibold text-ouro-600 transition-colors hover:bg-creme-200"
        >
          Editar
        </button>
        <button
          type="button"
          onClick={() => setAApagar(true)}
          className="rounded-full border border-blush/60 px-3 py-1 text-xs font-semibold text-tinta/80 transition-colors hover:bg-blush-300/30"
        >
          Apagar
        </button>
      </div>

      <Confirmacao
        aberto={aApagar}
        titulo="Apagar mensagem?"
        mensagem={`Apagar "${mensagem.titulo}"? A foto em destaque (se houver) fica no álbum.`}
        textoConfirmar="Apagar"
        destrutivo
        onCancelar={() => setAApagar(false)}
        onConfirmar={() => {
          setAApagar(false);
          void apagar(mensagem.id);
        }}
      />
    </article>
  );
}
